// ─── English prompts ────────────────────────────────────────────

export const FULL_REPORT_PROMPT = `Generate an 8,000-word in-depth Human Potential OS™ assessment report.

This must be a true long-form report, not a summary or outline.

Important:
- Do not write one short paragraph per section.
- Each section must contain detailed interpretation.
- Use the actual scores accurately.
- Do not merge states with different scores.
- Separate resource states from blocker states.
- Explain how the highest blocker may override the higher resource states and strongest traits.
- Include real-life examples, professional implications, relationship implications, coaching insights, and action steps.

Use this structure:

1. Opening Overview — 600–800 words
2. Executive Summary — 700–900 words
3. Characteristic Profile Analysis — 1,800–2,200 words
4. Being, Doing, and Having Pattern — 700–900 words
5. Harmonic Scale Analysis — 1,200–1,600 words
6. Resource States vs Blocker States — 700–900 words
7. Harmonic Override Pattern — 700–900 words
8. Trait-Emotion Interaction — 900–1,200 words
9. Key Strengths — 700–900 words
10. Breakthrough Zones — 800–1,000 words
11. Professional Insights — 900–1,200 words
12. Relationship Insights — 900–1,200 words
13. Coach Notes — 700–900 words
14. Action Plan — 700–900 words
15. Conclusion — 400–600 words

If you cannot complete the full 8,000 words in one response due to output limits, generate Part 1 only and end with:
"Continue generating Part 2."

Do not call the report complete until all parts are generated.`;

export const FULL_REPORT_STANDARD_PROMPT = `Generate a comprehensive Human Potential OS™ assessment report based on the respondent's Characteristic Profile and Harmonic Scale Profile.

This must be a rich, detailed, premium-quality analysis, not a short summary.

Write as an expert Human Potential OS™ coach, trainer, and assessment interpreter. The report should help the respondent feel deeply understood while also giving the coach meaningful insight into how to support them.

Use the assessment as a coaching and personal/professional development tool. Do not diagnose, clinically label, or make fixed claims about the person.

Important Harmonic Scale rule:
Do not simply identify the single highest Harmonic score as the dominant state. Separate the Harmonic Scale into:
1. Growth / Resource States: Interest, Optimism, Enthusiasm, Exhilaration.
2. Stabilising State: Rational Neutrality.
3. Blocker / Interference States: Antagonism, Open Anger, Passive Hostility, Fear, Grief, Hopelessness, Apathy.

The highest score from Interest and above shows the respondent's strongest available resource state.
The highest score from Antagonism and below shows the respondent's most important blocker or interference state.

Higher states show potential.
Lower states show what may block that potential.
The highest blocker often explains why the person is not fully living the potential shown by their strongest traits.

When a blocker state is high, explain how it may override, suppress, distort, or drain the person's higher traits and resource states.

The report must include:

1. Opening Overview
2. Executive Summary of the Profile
3. Characteristic Profile Analysis
4. Being, Doing, and Having Pattern
5. Harmonic Scale Analysis
6. Resource States vs Blocker States
7. Harmonic Override Pattern
8. Trait-Emotion Interaction
9. Key Strengths
10. Breakthrough Zones
11. Professional Insights
12. Relationship Insights
13. Coach Notes
14. Action Plan
15. Conclusion

Length: 5,000–8,000 words unless the user requests otherwise.

Use clear headings, warm professional language, and meaningful interpretation. Avoid generic AI language. Avoid shallow trait descriptions.`;

export const QUICK_TOPICS = [
  {
    label: 'Full Report',
    prompt: FULL_REPORT_PROMPT,
    isFullReport: true,
  },
  {
    label: 'Intro to Report',
    prompt: `Generate a warm, professional introduction to this Human Potential OS™ assessment report.

Explain the purpose of the Characteristic Profile, the Harmonic Scale Profile, and how the AI Analysis should be used as a coaching and development tool.

Explain that the Harmonic Scale is interpreted through resource states, blocker states, and the stabilising state of Rational Neutrality.

Make clear that higher states show available potential, while lower states may show what blocks or interferes with that potential.

Do not diagnose or label the respondent.

Length: 700–1,000 words.`,
  },
  {
    label: 'Trait Insights',
    prompt: `Analyze the respondent's Characteristic Profile scores in depth.

Explain the Being, Doing, and Having categories.

Identify the highest and lowest traits.

Interpret each trait in a balanced coaching-oriented way.

Do not just list scores. Explain the pattern.

For each major trait, explain:
- How it may show up at its best.
- How it may show up under pressure.
- How it interacts with other traits.
- How it may be amplified by resource states.
- How it may be suppressed or distorted by blocker states.

Length: 1,500–2,500 words.`,
  },
  {
    label: 'Harmonic Scale',
    prompt: `Analyze the respondent's Harmonic Scale Profile in depth.

Do not simply choose the single highest overall score as the dominant state.

Separate the analysis into:
1. Growth / Resource States: Interest, Optimism, Enthusiasm, Exhilaration.
2. Stabilising State: Rational Neutrality.
3. Blocker / Interference States: Antagonism, Open Anger, Passive Hostility, Fear, Grief, Hopelessness, Apathy.

Identify:
- Highest resource state.
- Highest blocker state.
- Secondary blockers.
- Rational Neutrality.
- Core Harmonic pattern.

Explain that higher states show available potential, while lower states show what may block that potential.

Explain how the highest blocker may override, suppress, distort, or drain the person's higher states and traits.

Use coaching language, not diagnostic language.

Length: 1,500–2,500 words.`,
  },
  {
    label: 'Key Strengths',
    prompt: `Identify the respondent's key strengths based on their highest Characteristic Profile scores and supportive Harmonic resource states.

Explain how these strengths may show up in personal life, professional life, leadership, communication, relationships, and self-development.

Also explain how the highest blocker state may suppress, distort, or drain these strengths under pressure.

Include overuse patterns and practical ways to leverage each strength.

Length: 1,200–2,000 words.`,
  },
  {
    label: 'Breakthrough Insights',
    prompt: `Identify the respondent's main breakthrough opportunities.

Use both the Characteristic Profile and the Harmonic Scale Profile.

Remember:
Higher states show potential.
Lower states show what may block that potential.
The highest blocker from Antagonism and below is one of the most important keys to the profile.

Classify traits into:
- Critical Breakthrough Zone.
- Development Breakthrough Zone.
- Growth / Leverage Zone.

A trait does not need to be low to be a breakthrough area. A high trait can become a breakthrough area if a blocker state suppresses, distorts, or overcharges it.

Explain the main coaching doorway and provide practical recommendations.

Length: 1,500–2,500 words.`,
  },
  {
    label: 'Professional Insights',
    prompt: `Explain how this respondent's profile may show up in professional contexts such as leadership, communication, decision-making, collaboration, productivity, adaptability, influence, conflict, creativity, strategic thinking, business, career, and performance.

Use the highest traits to identify professional strengths.

Use the highest blocker state to explain where those strengths may become inconsistent, reactive, delayed, emotionally charged, or underexpressed.

Provide practical workplace development recommendations.

Length: 1,500–2,500 words.`,
  },
  {
    label: 'Relationship Insights',
    prompt: `Explain how this respondent's profile may influence personal relationships, communication, emotional expression, trust, conflict, connection, boundaries, and relational growth.

Use the highest relationship-relevant traits such as Communication, Sociability, Integrity, Positive Outlook, Judgment, and Adaptability.

Use the highest blocker state to explain how lower emotional states may override or distort otherwise positive relational traits.

Provide supportive, practical recommendations.

Length: 1,500–2,500 words.`,
  },
  {
    label: 'Action Steps',
    prompt: `Create a practical action plan based on the respondent's Characteristic Profile and Harmonic Scale Profile.

Include:
- Immediate 7-day steps.
- 30-day development actions.
- 90-day growth direction.
- Reflection questions.
- Suggested coaching focus.
- Practical exercises.

The action plan should focus on:
1. Leveraging the person's strongest traits.
2. Accessing higher resource states more consistently.
3. Reducing the influence of the highest blocker state.
4. Preventing lower states from overriding positive traits and potential.

Length: 1,200–2,000 words.`,
  },
  {
    label: 'Coach Notes',
    prompt: `Generate coach-facing notes for a trained Human Potential OS™ practitioner.

Identify:
- The main profile story.
- Highest resource state.
- Highest blocker state.
- Secondary blockers.
- Likely coaching themes.
- Useful questions to ask.
- Strengths to leverage.
- Possible blind spots.
- Emotional patterns to approach gently.
- What not to over-assume.
- Suggested first-session structure.
- How to help the client reduce blocker-state interference.
- How to help the client access higher traits and resource states more consistently.

Length: 1,500–2,500 words.`,
  },
];

// ─── Turkish prompts ────────────────────────────────────────────

export const FULL_REPORT_PROMPT_TR = `8.000 kelimelik derinlemesine bir Human Potential OS™ değerlendirme raporu oluştur.

Bu kısa bir özet veya taslak değil, gerçek bir uzun biçimli rapor olmalıdır.

Önemli:
- Her bölüm için yalnızca bir kısa paragraf yazma.
- Her bölüm ayrıntılı yorum içermeli.
- Gerçek puanları doğru şekilde kullan.
- Farklı puanlara sahip durumları birleştirme.
- Kaynak durumlarını engelleyici durumlardan ayır.
- En yüksek engelleyicinin, daha yüksek kaynak durumlarını ve en güçlü özellikleri nasıl geçersiz kılabileceğini açıkla.
- Gerçek hayattan örnekler, profesyonel çıkarımlar, ilişki çıkarımları, koçluk içgörüleri ve eylem adımları dahil et.

Bu yapıyı kullan:

1. Açılış Genel Bakışı — 600–800 kelime
2. Yönetici Özeti — 700–900 kelime
3. Karakteristik Profil Analizi — 1.800–2.200 kelime
4. Varoluş, Yapma ve Sahip Olma Deseni — 700–900 kelime
5. Harmonik Ölçek Analizi — 1.200–1.600 kelime
6. Kaynak Durumları ve Engeller — 700–900 kelime
7. Harmonik Geçersiz Kılma Deseni — 700–900 kelime
8. Özellik-Duygu Etkileşimi — 900–1.200 kelime
9. Temel Güçlü Yönler — 700–900 kelime
10. Dönüşüm Alanları — 800–1.000 kelime
11. Profesyonel İçgörüler — 900–1.200 kelime
12. İlişki İçgörüleri — 900–1.200 kelime
13. Koç Notları — 700–900 kelime
14. Eylem Planı — 700–900 kelime
15. Sonuç — 400–600 kelime

Çıktı sınırları nedeniyle tüm 8.000 kelimeyi tek bir yanıta sığdıramazsan, yalnızca 1. Bölümü oluştur ve şu ifadeyle bitir:
"Continue generating Part 2."

Tüm bölümler oluşturulana kadar raporu tamamlanmış sayma.`;

export const FULL_REPORT_STANDARD_PROMPT_TR = `Danışanın Karakteristik Profili ve Harmonik Ölçek Profiline dayanan kapsamlı bir Human Potential OS™ değerlendirme raporu oluştur.

Bu kısa bir özet değil, zengin, ayrıntılı, premium kalitede bir analiz olmalıdır.

Bir Human Potential OS™ koçu, eğitmeni ve değerlendirme yorumcusu olarak yaz. Rapor, danışanın kendini derinden anlaşılmış hissetmesini sağlamalı ve aynı zamanda koça onları nasıl destekleyeceğine dair anlamlı bir içgörü vermelidir.

Değerlendirmeyi bir koçluk ve kişisel/profesyonel gelişim aracı olarak kullan. Kişiyi teşhis etme, klinik etiketleme veya kişi hakkında kesin iddialarda bulunma.

Önemli Harmonik Ölçek kuralı:
Tek en yüksek Harmonik skoru baskın durum olarak belirleme. Harmonik Ölçeği şu şekilde ayır:
1. Büyüme / Kaynak Durumları: İlgi, İyimserlik, Coşku, Coşkunluk.
2. Dengeleyici Durum: Rasyonel Tarafsızlık.
3. Engelleyici / Müdahale Durumları: Karşı Koyma, Açık Öfke, Pasif Düşmanlık, Korku, Keder, Umutsuzluk, İlgisizlik.

İlgi ve üzeri arasından en yüksek skor, danışanın en güçlü kullanılabilir kaynak durumunu gösterir.
Karşı Koyma ve aşağısı arasından en yüksek skor, danışanın en önemli engelini veya müdahale durumunu gösterir.

Yüksek durumlar potansiyeli gösterir.
Düşük durumlar bu potansiyeli neyin engelleyebileceğini gösterir.
En yüksek engelleyici genellikle kişinin en güçlü özelliklerinin gösterdiği potansiyeli neden tam yaşamadığını açıklar.

Bir engelleyici durum yüksek olduğunda, kişinin daha yüksek özelliklerini ve kaynak durumlarını nasıl geçersiz kılabileceğini, bastırabileceğini, çarpıtabileceğini veya tüketebileceğini açıkla.

Rapor şu bölümleri içermeli:

1. Açılış Genel Bakışı
2. Profil Yönetici Özeti
3. Karakteristik Profil Analizi
4. Varoluş, Yapma ve Sahip Olma Deseni
5. Harmonik Ölçek Analizi
6. Kaynak Durumları ve Engeller
7. Harmonik Geçersiz Kılma Deseni
8. Özellik-Duygu Etkileşimi
9. Temel Güçlü Yönler
10. Dönüşüm Alanları
11. Profesyonel İçgörüler
12. İlişki İçgörüleri
13. Koç Notları
14. Eylem Planı
15. Sonuç

Uzunluk: Kullanıcı aksini istemedikçe 5.000–8.000 kelime.

Net başlıklar, sıcak profesyonel dil ve anlamlı yorum kullan. Genel yapay zeka dilinden kaçın. Yüzeysel özellik açıklamalarından kaçın.`;

export const QUICK_TOPICS_TR = [
  {
    label: 'Tam Rapor',
    prompt: FULL_REPORT_PROMPT_TR,
    isFullReport: true,
  },
  {
    label: 'Raporun Girişi',
    prompt: `Bu Human Potential OS™ değerlendirme raporuna sıcak, profesyonel bir giriş oluştur.

Karakteristik Profil'in, Harmonik Ölçek Profili'nin amacını ve Yapay Zekâ Analizi'nin bir koçluk ve gelişim aracı olarak nasıl kullanılması gerektiğini açıkla.

Harmonik Ölçeğin kaynak durumları, engelleyici durumlar ve Rasyonel Tarafsızlık dengeleyici durumu aracılığıyla yorumlandığını açıkla.

Yüksek durumların kullanılabilir potansiyeli gösterdiğini, düşük durumların ise bu potansiyeli neyin engelleyebileceğini veya müdahale edebileceğini netleştir.

Danışanı teşhis etme veya etiketleme.

Uzunluk: 700–1.000 kelime.`,
  },
  {
    label: 'Özellik İçgörüleri',
    prompt: `Danışanın Karakteristik Profil puanlarını derinlemesine analiz et.

Varoluş, Yapma ve Sahip Olma kategorilerini açıkla.

En yüksek ve en düşük özellikleri belirle.

Her özelliği dengeli, koçluk odaklı bir şekilde yorumla.

Sadece puanları listeleme. Deseni açıkla.

Her büyük özellik için şunları açıkla:
- En iyisinde nasıl ortaya çıkabilir.
- Baskı altında nasıl ortaya çıkabilir.
- Diğer özelliklerle nasıl etkileşir.
- Kaynak durumları tarafından nasıl güçlendirilebilir.
- Engelleyici durumlar tarafından nasıl bastırılabilir veya çarpıtılabilir.

Uzunluk: 1.500–2.500 kelime.`,
  },
  {
    label: 'Harmonik Ölçek',
    prompt: `Danışanın Harmonik Ölçek Profilini derinlemesine analiz et.

Tek en yüksek genel skoru baskın durum olarak seçme.

Analizi şu şekilde ayır:
1. Büyüme / Kaynak Durumları: İlgi, İyimserlik, Coşku, Coşkunluk.
2. Dengeleyici Durum: Rasyonel Tarafsızlık.
3. Engelleyici / Müdahale Durumları: Karşı Koyma, Açık Öfke, Pasif Düşmanlık, Korku, Keder, Umutsuzluk, İlgisizlik.

Şunları belirle:
- En yüksek kaynak durumu.
- En yüksek engelleyici durum.
- İkincil engeller.
- Rasyonel Tarafsızlık.
- Temel Harmonik desen.

Yüksek durumların kullanılabilir potansiyeli gösterdiğini, düşük durumların bu potansiyeli neyin engelleyebileceğini açıkla.

En yüksek engelleyicinin kişinin daha yüksek durumlarını ve özelliklerini nasıl geçersiz kılabileceğini, bastırabileceğini, çarpıtabileceğini veya tüketebileceğini açıkla.

Koçluk dili kullan, teşhis dili kullanma.

Uzunluk: 1.500–2.500 kelime.`,
  },
  {
    label: 'Temel Güçlü Yönler',
    prompt: `Danışanın en yüksek Karakteristik Profil puanlarına ve destekleyici Harmonik kaynak durumlarına dayanan temel güçlü yönlerini belirle.

Bu güçlü yönlerin kişisel yaşamda, profesyonel yaşamda, liderlikte, iletişimde, ilişkilerde ve öz-gelişimde nasıl ortaya çıkabileceğini açıkla.

Ayrıca en yüksek engelleyici durumun bu güçlü yönleri baskı altında nasıl bastırabileceğini, çarpıtabileceğini veya tüketebileceğini de açıkla.

Aşırı kullanım desenlerini ve her güçlü yönü pratik yollarla nasıl kullanabileceğini dahil et.

Uzunluk: 1.200–2.000 kelime.`,
  },
  {
    label: 'Dönüşüm İçgörüleri',
    prompt: `Danışanın temel dönüşüm fırsatlarını belirle.

Hem Karakteristik Profili hem de Harmonik Ölçek Profilini kullan.

Unutma:
Yüksek durumlar potansiyeli gösterir.
Düşük durumlar bu potansiyeli neyin engelleyebileceğini gösterir.
Karşı Koyma ve aşağısı arasından en yüksek engelleyici, profile ilişkin en önemli anahtarlardan biridir.

Özellikleri şu şekilde sınıflandır:
- Kritik Dönüşüm Alanı.
- Gelişim Dönüşüm Alanı.
- Büyüme / Kullanım Alanı.

Bir özelliğin dönüşüm alanı olması için düşük olması gerekmez. Bir engelleyici durum yüksek bir özelliği bastırıyor, çarpıtıyor veya aşırı yüklüyorsa, o da dönüşüm alanı olabilir.

Ana koçluk kapısını açıkla ve pratik öneriler sun.

Uzunluk: 1.500–2.500 kelime.`,
  },
  {
    label: 'Profesyonel İçgörüler',
    prompt: `Bu danışanın profilinin liderlik, iletişim, karar verme, işbirliği, verimlilik, uyum, etki, çatışma, yaratıcılık, stratejik düşünme, iş, kariyer ve performans gibi profesyonel bağlamlarda nasıl ortaya çıkabileceğini açıkla.

En yüksek özellikleri kullanarak profesyonel güçlü yönleri belirle.

En yüksek engelleyici durumu kullanarak bu güçlü yönlerin nerede tutarsız, tepkisel, gecikmiş, duygusal olarak yüklü veya yetersiz ifade edilmiş hale gelebileceğini açıkla.

Pratik iş yeri gelişim önerileri sun.

Uzunluk: 1.500–2.500 kelime.`,
  },
  {
    label: 'İlişki İçgörüleri',
    prompt: `Bu danışanın profilinin kişisel ilişkileri, iletişimi, duygusal ifadeyi, güveni, çatışmayı, bağlantıyı, sınırları ve ilişkisel büyümeyi nasıl etkileyebileceğini açıkla.

İletişim, Sosyallik, Dürüstlük, Olumlu Bakış Açısı, Muhakeme ve Uyum Yeteneği gibi en yüksek ilişkiyle ilgili özellikleri kullan.

En yüksek engelleyici durumu kullanarak daha düşük duygusal durumların aksi halde olumlu ilişkisel özellikleri nasıl geçersiz kılabileceğini veya çarpıtabileceğini açıkla.

Destekleyici, pratik öneriler sun.

Uzunluk: 1.500–2.500 kelime.`,
  },
  {
    label: 'Eylem Adımları',
    prompt: `Danışanın Karakteristik Profili ve Harmonik Ölçek Profiline dayanan pratik bir eylem planı oluştur.

Şunları dahil et:
- Hemen 7 günlük adımlar.
- 30 günlük gelişim eylemleri.
- 90 günlük büyüme yönü.
- Refleksyon soruları.
- Önerilen koçluk odağı.
- Pratik egzersizler.

Eylem planı şu konulara odaklanmalı:
1. Kişinin en güçlü özelliklerini kullanma.
2. Daha yüksek kaynak durumlarına daha tutarlı şekilde erişme.
3. En yüksek engelleyici durumun etkisini azaltma.
4. Daha düşük durumların olumlu özellikleri ve potansiyeli geçersiz kılmasını önleme.

Uzunluk: 1.200–2.000 kelime.`,
  },
  {
    label: 'Koç Notları',
    prompt: `Eğitimli bir Human Potential OS™ uygulayıcısı için koça yönelik notlar oluştur.

Şunları belirle:
- Ana profil hikayesi.
- En yüksek kaynak durumu.
- En yüksek engelleyici durum.
- İkincil engeller.
- Olası koçluk temaları.
- Sorulması faydalı sorular.
- Kullanılacak güçlü yönler.
- Olası körlük noktaları.
- Dikkatli yaklaşılması gereken duygusal desenler.
- Fazla varsayımda bulunulmaması gerekenler.
- Önerilen ilk oturum yapısı.
- Danışanın engelleyici durum müdahalesini azaltmasına nasıl yardımcı olunur.
- Danışanın daha yüksek özelliklere ve kaynak durumlarına daha tutarlı erişmesine nasıl yardımcı olunur.

Uzunluk: 1.500–2.500 kelime.`,
  },
];

// ─── Locale helpers ─────────────────────────────────────────────

export type Locale = 'en' | 'tr';

export function getFullReportPrompt(locale: Locale): string {
  return locale === 'tr' ? FULL_REPORT_PROMPT_TR : FULL_REPORT_PROMPT;
}

export function getQuickTopics(locale: Locale) {
  return locale === 'tr' ? QUICK_TOPICS_TR : QUICK_TOPICS;
}

export function getContinuationPrompt(locale: Locale, nextPart: number): string {
  if (locale === 'tr') {
    return `Rapora kaldığın yerden devam et. Kalan bölümleri yazmaya devam et. Daha önce yazılan içeriği tekrarlama. Aynı derinliği ve kaliteyi koru. Bundan sonra daha fazla bölüm kalırsa, "Continue generating Part ${nextPart}." ile bitir. Bu son bölümse, sonucu yaz ve devam işareti ekleme.`;
  }
  return `Continue the report from where you left off. Continue writing the remaining sections. Do not repeat content already written. Maintain the same depth and quality. If there are more sections remaining after this, end with "Continue generating Part ${nextPart}." If this is the final part, write the conclusion and do not add a continuation marker.`;
}
